import { useEffect, useRef } from 'react';
import { Flex, Icon, Input, Text } from '@kognitos/lattice';
import {
  useUserPreferencesStore,
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
} from '@/shared/stores';
import type { FileInputProps } from '../../types';
import { parseFileValue } from '../../utils/fileUpload';
import { useAutomationInputFileUpload } from '../../hooks/useFileUpload';
import { useDragDrop } from '../../hooks/useDragDrop';
import { FileDropzone } from './FileDropzone';
import { FileList } from './FileList';
import { DefaultFileUploadArea } from './DefaultFileUploadArea';

/**
 * File input component with drag-and-drop upload support
 *
 * Provides a rich file upload experience with:
 * - Drag and drop functionality
 * - Click to browse files
 * - Single or multiple file upload
 * - Visual file type indicators with colored icons
 * - Upload progress feedback
 * - File removal capability
 * - Stores file metadata as JSON string
 *
 * Files are uploaded to the backend and referenced by resource name.
 * The component handles both new JSON format {name, resourceName} and legacy
 * string format for backward compatibility.
 *
 * @param props - Component props
 * @returns JSX element containing the file upload interface
 */
export function FileInput({
  name,
  value,
  onChange,
  error,
  required,
  multiple = false,
  disabled,
  fileHistory,
  onSelectHistoryFile,
  isDefault,
  singleColumn,
  compact = false,
}: FileInputProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const {
    uploadedFiles,
    setUploadedFiles,
    uploadError,
    isUploading,
    uploadingCount,
    processFiles,
    removeFile,
  } = useAutomationInputFileUpload({
    organizationId: currentOrganizationId,
    workspaceId: currentWorkspaceId,
    multiple,
    onChange,
  });

  // Drag and drop state
  const { isDragging, handleDragOver, handleDragLeave, handleDrop } =
    useDragDrop({
      onFilesDropped: processFiles,
      disabled: isUploading || disabled,
    });

  // Parse incoming value to set initial uploaded files
  // eslint-disable-next-line no-restricted-syntax -- reason: Parse file value prop to set initial uploaded files
  useEffect(() => {
    const parsedFiles = parseFileValue(value, multiple);
    setUploadedFiles(parsedFiles);
  }, [value, multiple, setUploadedFiles]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      processFiles(files);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const hasFiles = uploadedFiles.length > 0;

  // Render alternative layout for single default file inputs with history
  const isDefaultSingleFile = isDefault && !multiple;

  if (isDefaultSingleFile) {
    return (
      <Flex direction="column" gap={2} className="w-full">
        <DefaultFileUploadArea
          uploadedFiles={uploadedFiles}
          onRemove={removeFile}
          disabled={disabled}
          fileHistory={fileHistory}
          onSelectHistoryFile={onSelectHistoryFile}
          error={error}
        />

        {/* Upload error */}
        {uploadError && (
          <Flex gap={1} align="center">
            <Icon type="AlertCircle" color="error" />
            <Text level="small">{uploadError}</Text>
          </Flex>
        )}
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap={2} className="w-full">
      {/* Hidden file input */}
      <Input
        ref={fileInputRef}
        id={name}
        name={name}
        type="file"
        onChange={handleFileChange}
        required={required}
        aria-invalid={!!error || !!uploadError}
        disabled={isUploading || disabled}
        multiple={multiple}
        className="hidden"
        data-testid={`input-${name}`}
      />

      {/* Show large dropzone only when no files uploaded */}
      {!hasFiles && !disabled && (
        <FileDropzone
          compact={compact}
          isUploading={isUploading}
          uploadingCount={uploadingCount}
          isDragging={isDragging}
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          error={error}
        />
      )}

      {/* File list - shown when files are uploaded */}
      {hasFiles && (
        <FileList
          files={uploadedFiles}
          multiple={multiple}
          isUploading={isUploading}
          uploadingCount={uploadingCount}
          isDragging={isDragging}
          onRemove={removeFile}
          onUploadClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          disabled={disabled}
          singleColumn={singleColumn}
          fileHistory={fileHistory}
          onSelectHistoryFile={onSelectHistoryFile}
        />
      )}

      {/* Upload error */}
      {uploadError && (
        <Flex gap={1} align="center">
          <Icon type="AlertCircle" color="error" />
          <Text level="small">{uploadError}</Text>
        </Flex>
      )}
    </Flex>
  );
}
